import type { DependencyResolutionResult, TemplateAnalysisDependency } from "../types";
import { reactive } from "vue";
import { FALLBACK_LLM_MODEL, useDefaultModels } from "@/composables/useDefaultModels";
import {
  useCreateStructuredAnalysis,
  useStructuredAnalysesQuery,
} from "@/queries/structured-analysis.query";

interface ResolverState {
  isResolving: boolean;
  current: number;
  total: number;
  errors: string[];
}

/**
 * Hook para resolver dependências de StructuredAnalysis dos templates
 */
export function useDependencyResolver() {
  const createAnalysisMutation = useCreateStructuredAnalysis();
  const analysesQuery = useStructuredAnalysesQuery();
  const { defaultLlmModel } = useDefaultModels();

  const state = reactive<ResolverState>({
    isResolving: false,
    current: 0,
    total: 0,
    errors: [],
  });

  /**
   * Resolve dependências de StructuredAnalysis
   * - Verifica se já existe uma análise com o mesmo nome
   * - Se não existir, cria automaticamente
   */
  async function resolveDependencies(
    dependencies: TemplateAnalysisDependency[]
  ): Promise<DependencyResolutionResult> {
    state.isResolving = true;
    state.total = dependencies.length;
    state.current = 0;
    state.errors = [];

    const createdIds: string[] = [];
    const existingAnalyses = analysesQuery.data.value ?? [];
    // Modelo resolvido dinamicamente do catálogo; templates não trazem mais `model`.
    const model = defaultLlmModel.value ?? FALLBACK_LLM_MODEL;

    for (const dep of dependencies) {
      state.current++;

      try {
        // Verificar se já existe uma análise com mesmo nome (case-insensitive)
        const existing = existingAnalyses.find(
          (a) => a.name.toLowerCase() === dep.data.name.toLowerCase()
        );

        if (existing) {
          createdIds.push(existing.id);
          continue;
        }

        // Criar nova análise
        const result = await createAnalysisMutation.mutateAsync({
          ...dep.data,
          model,
          status: "active",
        });
        createdIds.push(result.id);
      } catch (error) {
        console.error(`Error creating analysis ${dep.key}:`, error);
        state.errors.push(dep.key);
      }
    }

    state.isResolving = false;

    return {
      createdIds,
      errors: state.errors,
    };
  }

  /**
   * Reset do estado
   */
  function reset() {
    state.isResolving = false;
    state.current = 0;
    state.total = 0;
    state.errors = [];
  }

  return {
    resolveDependencies,
    reset,
    state,
  };
}
